import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import ComponentCard from '../../components/common/ComponentCard';
import PageMeta from '../../components/common/PageMeta';
import { News, useNewsStore } from '../../store/useNewsStore';
import { Button, Label } from 'flowbite-react';
import { HiArrowLeft } from 'react-icons/hi';
import dayjs from 'dayjs';

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const getNewsById = useNewsStore((state) => state.getNewsById);
  const loading = useNewsStore((state) => state.loading);

  const [news, setNews] = useState<News | null>(null);

  useEffect(() => {
    if (id) {
      loadNews();
    }
  }, [id]);

  const loadNews = async () => {
    if (!id) return;
    const newsData = await getNewsById(id);
    if (newsData) {
      setNews(newsData);
    }
  };

  if (!news) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-500" style={{ fontFamily: 'Lato, sans-serif' }}>
            {loading ? 'Loading...' : 'News not found'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageMeta title="News Details" description="View news information" />
      <PageBreadcrumb pageTitle="News Details" />
      <div className="space-y-6">
        <ComponentCard title="News Information">
          <div className="space-y-6">
            {/* Header with Edit Button */}
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {news.title}
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                  Published: {news.date}
                </p>
              </div>
              <Button
                onClick={() => navigate(`/news`)}
                className="bg-[#B8860B] text-white hover:bg-[#9A7209]"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  <HiArrowLeft className="mr-2" /> Back to News
                </span>
              </Button>
            </div>

            {/* News Image */}
            {news.image && (
              <div className="w-full">
                <img src={news.image} alt={news.title} className="w-full h-96 object-cover rounded-lg" />
              </div>
            )}

            {/* News Information */}
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label value="Title" style={{ fontFamily: 'Lato, sans-serif' }} />
                <p className="text-gray-900 dark:text-white mt-1 text-lg" style={{ fontFamily: 'Lato, sans-serif' }}>
                  {news.title}
                </p>
              </div>
              <div>
                <Label value="Description" style={{ fontFamily: 'Lato, sans-serif' }} />
                <p className="text-gray-900 dark:text-white mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                  {news.description}
                </p>
              </div>
              <div>
                <Label value="Full Content" style={{ fontFamily: 'Lato, sans-serif' }} />
                <div
                  className="text-gray-900 dark:text-white mt-1 prose max-w-none"
                  style={{ fontFamily: 'Lato, sans-serif' }}
                  dangerouslySetInnerHTML={{ __html: news.fullContent }}
                />
              </div>
              <div>
                <Label value="Date" style={{ fontFamily: 'Lato, sans-serif' }} />
                <p className="text-gray-900 dark:text-white mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                  {news.date}
                </p>
              </div>
              <div>
                <Label value="Created At" style={{ fontFamily: 'Lato, sans-serif' }} />
                <p className="text-gray-900 dark:text-white mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                  {dayjs(news.createdAt).format('DD MMM YYYY, hh:mm A')}
                </p>
              </div>
            </div>
          </div>
        </ComponentCard>
      </div>
    </>
  );
}
